import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { Responsibility } from 'src/app/models/responsibility';

@Component({
  selector: 'app-responsibility-multi-select',
  standalone: false,
  templateUrl: './responsibility-multi-select.component.html',
  styleUrls: ['./responsibility-multi-select.component.scss'],
})
export class ResponsibilityMultiSelectComponent {
  @Input({ required: true }) control!: FormControl<Responsibility[]>;
  @Input({ required: true }) filteredResponsibilities$!: Observable<Responsibility[]>;

  @Input() labelKey = 'JobResponsibilities';
  @Input() searchPlaceholderKey = 'Search';
  @Input() invalidFieldKey = 'InvalidField';

  @Input() tooltipKeyDependentNeedsBase = 'Responsibility.Tooltip.DependentNeedsBase';
  @Input() tooltipKeyBaseLockedByDependents = 'Responsibility.Tooltip.BaseLockedByDependents';

  @Output() filterChanged = new EventEmitter<string>();

  public compareResponsibilities(a: Responsibility, b: Responsibility): boolean {
    return a && b ? a.Id === b.Id : a === b;
  }

  public drop(event: CdkDragDrop<Responsibility[]>): void {
    const current = this.control.value ?? [];
    moveItemInArray(current, event.previousIndex, event.currentIndex);
    this.control.markAsDirty();
    this.control.setValue([...current]);
  }

  public onFiltered(value: string): void {
    this.filterChanged.emit(value);
    this.control.setValue(this.control.value);
  }

  public onResponsibilityRemoved(item: Responsibility): void {
    const current = this.control.value ?? [];

    if (!this.canRemoveResponsibility(item, current)) {
      return;
    }

    const updated = current.filter((x) => x.Id !== item.Id);
    this.control.markAsDirty();
    this.control.setValue(updated);
  }

  public canRemoveResponsibility(item: Responsibility, selected?: Responsibility[]): boolean {
    const list = selected ?? (this.control.value ?? []);
    return !this.isLastSelectedBaseAndHasDependents(item, list);
  }

  public getDisabledReasonKey(item: Responsibility): string | null {
    const selected = this.control.value ?? [];

    if (!!item?.RequiresBaseResponsibility && !selected.some((x) => !x.RequiresBaseResponsibility)) {
      return this.tooltipKeyDependentNeedsBase;
    }

    if (this.isLastSelectedBaseAndHasDependents(item, selected)) {
      return this.tooltipKeyBaseLockedByDependents;
    }

    return null;
  }

  public onOptionToggled(event: any, item: Responsibility, reasonKey: string | null): void {
    if (!event?.isUserInput) {
      return;
    }

    if (!reasonKey) {
      return;
    }

    if (event.source.selected) {
      event.source.deselect();
    } else {
      event.source.select();
    }
  }

  private isLastSelectedBaseAndHasDependents(item: Responsibility, selected: Responsibility[]): boolean {
    if (!item) {
      return false;
    }

    if (!!item.RequiresBaseResponsibility) {
      return false;
    }

    const isSelected = selected.some((x) => x.Id === item.Id);
    if (!isSelected) {
      return false;
    }

    const baseCount = selected.filter((x) => !x.RequiresBaseResponsibility).length;
    const hasDependents = selected.some((x) => !!x.RequiresBaseResponsibility);

    return baseCount === 1 && hasDependents;
  }
}
